import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import BlogRelatedStrip from '../../../components/feature/BlogRelatedStrip';
import BlogProgressBar from '../../../components/feature/BlogProgressBar';

const SITE_URL = 'https://hoosierlifeinsurance.com';
const CANONICAL = `${SITE_URL}/blog/burial-insurance-indiana`;
const OG_IMAGE = 'https://public.readdy.ai/ai/img_res/6d9212afa7b1575ab7a10df3d43b6357.jpg';

export default function BurialInsuranceIndiana() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Burial Insurance Indiana: Costs, Coverage & Plans 2025';

    // Set canonical URL
    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.rel = 'canonical';
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.href = CANONICAL;

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Burial Insurance Indiana: What It Costs, What It Covers & The Best Plans for Hoosier Seniors (2025)',
        description: 'Compare burial insurance plans in Indiana for 2025. Real rates for ages 60–85, top carriers, guaranteed issue options, and how to avoid the most common mistakes when buying final expense coverage.',
        author: { '@type': 'Person', name: 'Benjamin Wright' },
        publisher: { '@type': 'Organization', name: 'Hoosier Life Insurance', url: SITE_URL },
        datePublished: '2025-03-18T00:00:00Z',
        dateModified: new Date().toISOString(),
        url: CANONICAL,
        mainEntityOfPage: 'https://hoosierlifeinsurance.com/blog/burial-insurance-indiana',
        image: { '@type': 'ImageObject', url: 'https://readdy.ai/api/search-image?query=dignified%20senior%20Indiana%20couple%20in%20their%2070s%20sitting%20together%20at%20home%20reviewing%20burial%20insurance%20final%20expense%20policy%20documents%20peaceful%20warm%20living%20room%20clean%20natural%20light%20soft%20bokeh%20background%20compassionate%20planning&width=1200&height=630&seq=blog-burial-og&orientation=landscape', width: 1200, height: 630 },
        timeRequired: 'PT10M',
        wordCount: 2000,
        inLanguage: 'en-US',
        isPartOf: {
          '@type': 'WebSite',
          url: SITE_URL
        },
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', 'h2', 'details summary']
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How much does burial insurance cost in Indiana?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'A $10,000 burial insurance policy in Indiana typically costs $40–$95/month for a 65-year-old depending on gender and health. A $20,000 policy runs $75–$180/month. Rates increase significantly with age — a 75-year-old might pay $120–$240/month for $10,000 in coverage.',
            },
          },
          {
            '@type': 'Question',
            name: 'What is the average funeral cost in Indiana?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'The average funeral in Indiana costs $8,500–$12,000 including burial, service, and a casket. Cremation with a service runs $3,000–$5,500. These costs have risen about 3.5% per year, meaning a $10,000 policy purchased today may only cover a portion of costs in 10–15 years. Most advisors recommend $15,000–$25,000 in burial insurance to account for inflation.',
            },
          },
          {
            '@type': 'Question',
            name: 'Can I get burial insurance in Indiana with health problems?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes. Guaranteed issue burial insurance accepts everyone aged 50–85 regardless of health. There are no medical questions and no exam. The tradeoff is a 2-year graded benefit period and higher premiums per dollar of coverage. Simplified issue burial insurance has health questions but is less expensive and available for those in moderately good health.',
            },
          },
          {
            '@type': 'Question',
            name: 'Is burial insurance the same as final expense insurance?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes — "burial insurance," "final expense insurance," and "funeral insurance" all refer to the same type of product: small whole life policies (typically $5,000–$35,000) designed to cover end-of-life costs. The death benefit can be used for any purpose — funeral, burial, medical bills, or leaving a small gift to family.',
            },
          },
        ],
      },
    ];

    schemas.forEach((schema, idx) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = `schema-burial-${idx}`;
      el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
    });

    return () => {
      schemas.forEach((_, idx) => document.getElementById(`schema-burial-${idx}`)?.remove());
    };
  }, []);

  const rateTable = [
    { age: 60, female10k: '$38', male10k: '$48', female20k: '$72', male20k: '$92' },
    { age: 65, female10k: '$52', male10k: '$68', female20k: '$100', male20k: '$132' },
    { age: 70, female10k: '$72', male10k: '$95', female20k: '$140', male20k: '$186' },
    { age: 75, female10k: '$102', male10k: '$130', female20k: '$200', male20k: '$256' },
    { age: 80, female10k: '$148', male10k: '$190', female20k: '$292', male20k: '$376' },
  ];

  const carriers = [
    { name: 'Mutual of Omaha', coverage: '$2K–$25K', ages: '45–85', healthQ: 'Yes', gradedPeriod: 'No', amBest: 'A+', highlight: 'Best overall value' },
    { name: 'AIG / American General', coverage: '$5K–$25K', ages: '50–85', healthQ: 'No', gradedPeriod: '2 years', amBest: 'A', highlight: 'Best for health issues' },
    { name: 'Foresters Financial', coverage: '$5K–$35K', ages: '50–80', healthQ: 'Yes', gradedPeriod: 'No', amBest: 'A', highlight: 'Best for smokers' },
    { name: 'Transamerica', coverage: '$1K–$50K', ages: '45–85', healthQ: 'Yes', gradedPeriod: 'Partial', amBest: 'A', highlight: 'Highest coverage limit' },
    { name: 'Royal Neighbors', coverage: '$5K–$30K', ages: '50–80', healthQ: 'Yes', gradedPeriod: 'No', amBest: 'A', highlight: 'Best for women' },
    { name: 'Gerber Life', coverage: '$5K–$25K', ages: '50–80', healthQ: 'No', gradedPeriod: '2 years', amBest: 'A', highlight: 'Easiest approval' },
  ];

  const whatItCovers = [
    { icon: 'ri-service-line', title: 'Funeral home services', desc: 'Embalming, preparation, viewing, and staff services ($2,500–$4,500)' },
    { icon: 'ri-box-3-line', title: 'Casket or urn', desc: 'Traditional casket ($2,000–$5,000) or cremation urn ($200–$1,500)' },
    { icon: 'ri-map-pin-line', title: 'Cemetery plot & burial', desc: 'Plot, opening, closing, and monument fees ($1,500–$4,000)' },
    { icon: 'ri-flower-line', title: 'Flowers and obituary', desc: 'Arrangements, printing, online memorial ($500–$1,500)' },
    { icon: 'ri-hospital-line', title: 'Final medical bills', desc: 'Outstanding copays, deductibles, and end-of-life care costs' },
    { icon: 'ri-gift-line', title: 'A gift to family', desc: 'Any remaining death benefit paid directly to your beneficiary, tax-free' },
  ];

  return (
    <>
      <title>Burial Insurance Indiana: Costs, Coverage &amp; Plans 2026 | Hoosier Life</title>
      <meta name="description" content="Compare burial insurance in Indiana for 2026. Real rates for $10K–$25K policies, guaranteed approval options, top carriers & how Indiana seniors avoid overpaying for funeral coverage." />
      <meta name="keywords" content="burial insurance Indiana, burial insurance Indianapolis, final expense insurance Indiana, funeral insurance Indiana, burial policy Indiana seniors" />
      <meta property="og:title" content="Burial Insurance Indiana: Costs, Coverage & Best Plans (2025)" />
      <meta property="og:description" content="Real burial insurance rates for Indiana seniors. $10K–$25K coverage, guaranteed approval available, no medical exam required. Reviewed by a licensed Indiana agent." />
      <meta property="og:url" content={CANONICAL} />
      <link rel="canonical" href={CANONICAL} />
      <meta name="robots" content="index, follow" />
      <meta property="og:type" content="article" />
      <meta property="og:image" content={OG_IMAGE} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content={OG_IMAGE} />

      <BlogProgressBar />
      <Header />
      <article className="min-h-screen bg-white">

        {/* Hero */}
        <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white pt-28 pb-16">
          <div className="max-w-4xl mx-auto px-6">
            <Link to="/blog" className="inline-flex items-center text-white/60 hover:text-[#D97642] mb-6 transition-colors text-sm cursor-pointer">
              <i className="ri-arrow-left-line mr-2"></i>Back to Blog
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-amber-400/20 text-amber-300 text-xs font-bold px-3 py-1.5 rounded-full">Final Expense</span>
              <span className="text-white/50 text-xs">10 min read · March 2025</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-5 leading-tight">Burial Insurance in Indiana: Costs, Coverage &amp; Best Plans for 2026</h1>
            <p className="text-white/75 text-xl leading-relaxed max-w-2xl">The average Indiana funeral costs $9,500. Most seniors don't have that sitting in a savings account. Burial insurance solves that — here's everything you need to know before you buy.</p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/book-appointment" className="px-6 py-3 bg-[#D97642] text-white rounded-lg font-bold hover:bg-[#C26638] transition-colors whitespace-nowrap cursor-pointer">Get Free Quote</Link>
              <a href="tel:+13176482983" className="px-6 py-3 bg-white/10 text-white rounded-lg font-semibold border border-white/20 hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer">Call (317) 648-2983</a>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-12">

          {/* Quick Answer */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-xl mb-10">
            <p className="font-bold text-amber-800 mb-2 flex items-center gap-2"><i className="ri-lightbulb-flash-line text-amber-500 text-lg"></i>Quick Answer</p>
            <p className="text-amber-700 leading-relaxed text-[15px]">
              Burial insurance in Indiana typically costs <strong>$38–$190/month</strong> for $10,000 in coverage depending on your age and gender. Most Indiana seniors buy $15,000–$25,000 to cover the full cost of a funeral plus final medical bills. No medical exam required — you can get approved today regardless of age (50–85) or health history. For the full senior life insurance picture, also see our guide to <Link to="/blog/life-insurance-indiana-seniors" className="text-amber-700 hover:text-amber-800 font-semibold underline">life insurance for Indiana seniors</Link>.
            </p>
          </div>

          {/* Key Takeaways */}
          <div className="bg-[#1A3A52] text-white rounded-2xl p-7 mb-10">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <i className="ri-bookmark-3-fill text-[#D97642] text-xl"></i>
              Key Takeaways
            </h2>
            <ul className="space-y-2.5">
              {[
                'The average Indiana funeral costs $9,500–$11,650 for burial; cremation with a service runs $4,550 — Medicare covers $0 of either',
                'Burial insurance is whole life insurance in small amounts ($5K–$35K): premiums never increase, coverage never expires, no medical exam required',
                'Simplified issue (health questions, no exam) costs 20–35% less than guaranteed issue — always try simplified issue first',
                'A 65-year-old Indiana senior pays $52–$68/month for $10,000 in simplified issue coverage; $88–$132/month for guaranteed issue',
                'Buying from TV ads (Colonial Penn, AARP) typically means paying 30–50% more than comparable coverage shopped through an independent agent',
              ].map((point, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-white/90">
                  <i className="ri-check-line text-[#D97642] mt-0.5 flex-shrink-0"></i>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          {/* Intro */}
          <p className="text-gray-700 text-lg leading-relaxed mb-4">
            Nobody enjoys planning for their own funeral. But the alternative — leaving your family scrambling for $10,000+ while they're grieving — is far worse. Burial insurance, also called <Link to="/blog/final-expense-insurance-indianapolis" className="text-[#D97642] hover:text-[#C26638] font-semibold underline">final expense</Link> or funeral insurance, is a straightforward small whole life policy designed to cover exactly that: the cost of dying with dignity.
          </p>
          <p className="text-gray-700 text-lg leading-relaxed mb-10">
            This guide gives Indiana seniors an honest, plain-English breakdown of what burial insurance costs in 2025, which carriers offer the best value, and the mistakes to avoid that cost families thousands of dollars. For seniors who've been declined elsewhere or have health issues, <Link to="/blog/no-medical-exam-life-insurance-indiana" className="text-[#D97642] hover:text-[#C26638] font-semibold underline">guaranteed issue life insurance in Indiana</Link> is also worth reviewing.
          </p>

          {/* What is it */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">What Is Burial Insurance and How Does It Work?</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> Burial insurance is a small whole life policy ($5,000–$35,000) with no medical exam, designed specifically to cover funeral and final expenses. Unlike term life, it never expires. Premiums never increase. The death benefit is paid tax-free to your beneficiary, who can use it for any purpose — not just funeral costs. Two types: simplified issue (health questions) and guaranteed issue (zero questions).
            </div>
            <p className="text-gray-600 mb-6">Burial insurance is simply a small whole life insurance policy. Here's what makes it different from other life insurance:</p>
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              {[
                { icon: 'ri-money-dollar-circle-line', title: 'Small coverage amounts', desc: 'Typically $5,000–$35,000 — enough to cover a funeral and final bills, not income replacement.' },
                { icon: 'ri-user-heart-line', title: 'No medical exam', desc: 'Simplified issue (health questions) or guaranteed issue (no questions). Most seniors qualify.' },
                { icon: 'ri-infinity-line', title: 'Whole life policy', desc: 'Coverage never expires and premiums never increase — as long as you pay, you\'re covered.' },
              ].map((f, i) => (
                <div key={i} className="bg-[#1A3A52]/5 rounded-xl p-5 text-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#1A3A52] rounded-xl mx-auto mb-3">
                    <i className={`${f.icon} text-white text-xl`}></i>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2 text-sm">{f.title}</h3>
                  <p className="text-gray-600 text-xs leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-gray-700 leading-relaxed">When you die, your beneficiary — usually a spouse or child — receives the death benefit as a tax-free lump sum. They can use it for the funeral, burial, medical bills, or any other purpose. There are no restrictions on how the money is spent.</p>
          </section>

          {/* Indiana funeral costs */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">What Does a Funeral Cost in Indiana in 2025?</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> A traditional burial in Indiana averages $11,650 in 2025. Direct cremation costs about $1,800, while cremation with a full memorial service runs $4,550. Costs have risen 3.5%/year — a $10,000 policy purchased today will only cover about 70% of a standard funeral in 10 years. Most advisors recommend $15,000–$25,000 in burial insurance to account for inflation.
            </div>
            <p className="text-gray-600 mb-6">Understanding the real cost of a funeral in Indiana helps you choose the right coverage amount:</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200 mb-5">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    <th className="px-5 py-3 text-left font-semibold">Service</th>
                    <th className="px-5 py-3 text-left font-semibold">Burial (Average)</th>
                    <th className="px-5 py-3 text-left font-semibold">Cremation (Average)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { service: 'Funeral home basic services', burial: '$2,400', cremation: '$1,800' },
                    { service: 'Casket / Urn', burial: '$2,800', cremation: '$400' },
                    { service: 'Cemetery plot & burial fees', burial: '$2,500', cremation: '$800 (niche)' },
                    { service: 'Embalming & preparation', burial: '$800', cremation: 'N/A' },
                    { service: 'Viewing / visitation', burial: '$500', cremation: '$300' },
                    { service: 'Death certificates (avg. 6)', burial: '$150', cremation: '$150' },
                    { service: 'Flowers & obituary', burial: '$700', cremation: '$500' },
                    { service: 'Monument / marker', burial: '$1,800', cremation: '$600' },
                    { service: 'Total Estimated Cost', burial: '$11,650', cremation: '$4,550' },
                  ].map((r, i) => (
                    <tr key={i} className={r.service.startsWith('Total') ? 'bg-[#D97642]/10 font-bold' : i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-5 py-3 text-gray-900">{r.service}</td>
                      <td className="px-5 py-3 text-gray-700">{r.burial}</td>
                      <td className="px-5 py-3 text-gray-700">{r.cremation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-[#1A3A52]/5 border border-[#1A3A52]/15 rounded-xl p-4 text-sm text-gray-700">
              <strong>Recommendation:</strong> Buy at least $15,000 in burial insurance for traditional burial or $8,000 for cremation — and add a buffer for medical bills and inflation. Funeral costs in Indiana have risen 3.5%/year on average.
            </div>
          </section>

          {/* What it covers */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">What Does Burial Insurance Cover?</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {whatItCovers.map((item, i) => (
                <div key={i} className="flex items-start gap-4 bg-white border border-gray-100 rounded-xl p-4 hover:border-[#D97642]/30 transition-colors">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#D97642]/10 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-[#D97642] text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm mb-1">{item.title}</h3>
                    <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Rate table */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Indiana Burial Insurance Rates by Age (2025)</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> A 65-year-old Indiana female pays $52/month for $10,000 of simplified issue burial insurance; a male the same age pays $68/month. For $20,000 in coverage, the female rate is $100/month and male is $132/month. Guaranteed issue rates run 20–35% higher for the same coverage amounts.
            </div>
            <p className="text-gray-600 mb-6">Estimated monthly premiums for simplified issue whole life policies — Indiana residents, non-smoker rates:</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    <th className="px-4 py-3 text-left font-semibold">Age</th>
                    <th className="px-4 py-3 text-left font-semibold">Female / $10K</th>
                    <th className="px-4 py-3 text-left font-semibold">Male / $10K</th>
                    <th className="px-4 py-3 text-left font-semibold">Female / $20K</th>
                    <th className="px-4 py-3 text-left font-semibold">Male / $20K</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rateTable.map((r, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 font-bold text-gray-900">{r.age}</td>
                      <td className="px-4 py-3 text-[#D97642] font-semibold">{r.female10k}</td>
                      <td className="px-4 py-3 text-[#D97642] font-semibold">{r.male10k}</td>
                      <td className="px-4 py-3 text-[#1A3A52] font-semibold">{r.female20k}</td>
                      <td className="px-4 py-3 text-[#1A3A52] font-semibold">{r.male20k}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-2">*Estimated 2025 rates, simplified issue (health questions required). Guaranteed issue rates are 20–35% higher.</p>
          </section>

          {/* Carrier table */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Best Burial Insurance Carriers in Indiana (2025)</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> Mutual of Omaha (A+) is the best overall value for Indiana seniors who can answer health questions. AIG and Gerber Life offer guaranteed issue for those who can't qualify for simplified. Foresters Financial is best for tobacco users. Royal Neighbors typically offers the most competitive rates for Indiana women.
            </div>
            <p className="text-gray-600 mb-6">These carriers are actively writing burial and final expense policies for Indiana seniors with strong financial ratings:</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    {['Carrier', 'Coverage Range', 'Ages', 'Health Qs?', 'Graded Period', 'AM Best', 'Best For'].map(h => (
                      <th key={h} className="px-4 py-3 text-left font-semibold whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {carriers.map((r, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 font-bold text-[#1A3A52]">{r.name}</td>
                      <td className="px-4 py-3 text-gray-700">{r.coverage}</td>
                      <td className="px-4 py-3 text-gray-600">{r.ages}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${r.healthQ === 'Yes' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>{r.healthQ}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{r.gradedPeriod}</td>
                      <td className="px-4 py-3 font-bold text-emerald-700">{r.amBest}</td>
                      <td className="px-4 py-3 text-[#D97642] font-medium text-xs">{r.highlight}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* 5 mistakes */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">5 Burial Insurance Mistakes Indiana Seniors Make</h2>
            <div className="space-y-4">
              {[
                { icon: 'ri-close-circle-line', title: 'Buying from a TV ad without comparing', body: 'Colonial Penn and AARP mail and TV ads are notorious for overpricing. Their guaranteed issue rates are typically 30–50% higher than comparable carrier coverage. Always compare at least 3 carriers.' },
                { icon: 'ri-close-circle-line', title: 'Buying too little coverage', body: 'A $5,000 policy covers a cremation, not a full funeral and burial. Most Indiana families need $12,000–$20,000. Don\'t leave your family with a shortfall while they\'re grieving.' },
                { icon: 'ri-close-circle-line', title: 'Paying for guaranteed issue when you qualify for simplified', body: 'Simplified issue costs 20–35% less for the same coverage. If you\'re in reasonably good health, you can likely qualify. Don\'t pay the guaranteed issue premium unless you actually need it.' },
                { icon: 'ri-close-circle-line', title: 'Not understanding the graded benefit period', body: 'Guaranteed issue policies pay back only premiums in the first 2 years for natural death. If you\'re in poor health and only have 1–2 years to live, this policy may not pay out as expected. Be informed before you buy.' },
                { icon: 'ri-close-circle-line', title: 'Pre-paying the funeral home instead of buying insurance', body: 'Pre-need funeral contracts lock you in to one funeral home, can be difficult to transfer if you move, and don\'t leave anything for medical bills or gifts to family. A burial insurance policy gives your family flexibility.' },
              ].map((m, i) => (
                <div key={i} className="bg-red-50 border-l-4 border-red-400 p-5 rounded-r-xl">
                  <h3 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
                    <i className={`${m.icon} text-red-500 flex-shrink-0`}></i>{m.title}
                  </h3>
                  <p className="text-gray-700 text-sm leading-relaxed pl-6">{m.body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Author Bio */}
          <div className="border border-gray-200 rounded-2xl p-7 my-8 flex items-start gap-5">
            <div className="w-14 h-14 flex items-center justify-center bg-[#1A3A52] rounded-full flex-shrink-0">
              <i className="ri-shield-user-line text-white text-2xl"></i>
            </div>
            <div>
              <p className="font-bold text-gray-900 text-base mb-1">Written by the Licensed Life Insurance Specialists at Hoosier Life Insurance</p>
              <p className="text-sm text-gray-600 leading-relaxed mb-2">Our licensed Indiana agents compare burial insurance carriers for seniors across all of Indiana. We shop Mutual of Omaha, AIG, Foresters, Gerber Life, and others to find the best rate for your age and health status — never the TV advertised rates that overcharge by 30–50%.</p>
              <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Licensed in Indiana</span>
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Independent — 20+ Carriers</span>
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Last reviewed March 2025</span>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {[
                { q: 'How much does burial insurance cost in Indiana?', a: 'A $10,000 policy typically costs $38–$95/month for a 65-year-old depending on gender and health class. A $20,000 policy runs $75–$180/month. Rates increase with age. A 75-year-old might pay $130–$256/month for the same $20,000 in coverage.' },
                { q: 'Is burial insurance the same as life insurance?', a: 'Burial insurance IS life insurance — specifically a small whole life policy. "Burial insurance," "final expense insurance," and "funeral insurance" are all marketing terms for the same type of product. The death benefit can be used for any purpose, not just funeral costs.' },
                { q: 'Can I get burial insurance in Indiana with serious health problems?', a: 'Yes. Guaranteed issue burial insurance accepts everyone aged 50–85 with no health questions. Carriers like AIG, Gerber Life, and Transamerica offer guaranteed issue policies in Indiana. The tradeoff is a 2-year graded benefit period and premiums that are 20–35% higher than simplified issue.' },
                { q: 'At what age should I buy burial insurance?', a: 'As soon as possible after age 60 — ideally by 65. Every year you wait increases your premium significantly. A $20,000 policy that costs $100/month at 65 costs $186/month at 70 and $292/month at 80. Early buyers lock in lower rates for life.' },
              ].map((item, i) => (
                <div key={i} className="border border-gray-200 rounded-xl p-5">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                    <i className="ri-question-line text-[#D97642] mt-0.5 flex-shrink-0"></i>{item.q}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed pl-6">{item.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-2xl p-8 text-center text-white mb-12">
            <div className="w-14 h-14 flex items-center justify-center bg-amber-400/20 rounded-full mx-auto mb-4">
              <i className="ri-shield-check-line text-amber-400 text-2xl"></i>
            </div>
            <h2 className="text-2xl font-bold mb-3">Protect Your Family From Final Expense Costs</h2>
            <p className="text-white/70 mb-6 max-w-xl mx-auto">We'll shop Indiana's top burial insurance carriers and find you the best rate — guaranteed issue or simplified issue depending on your health. Free comparison with no obligation.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/book-appointment" className="px-6 py-3 bg-[#D97642] text-white rounded-lg font-bold hover:bg-[#C26638] transition-colors whitespace-nowrap cursor-pointer">Get Free Rate Comparison</Link>
              <a href="tel:+13176482983" className="px-6 py-3 bg-white/10 text-white rounded-lg font-bold hover:bg-white/20 border border-white/20 transition-colors whitespace-nowrap cursor-pointer">Call (317) 648-2983</a>
            </div>
          </section>

        </div>
      </article>

      <BlogRelatedStrip currentSlug="burial-insurance-indiana" />
      <Footer />
    </>
  );
}
